import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { TestRunService } from '../../services/test-run.service';
import { TestCaseRunHistory, TestCaseRunHistoryEntry } from '../../models/test-case-run-history.model';
import {
  environmentClass,
  matrixIcon,
  sourceToSystemKey,
  statusKey,
  systemMeta,
} from '../test-results/test-results.types';
import { formatDurationSeconds } from '../../shared/duration.util';
import { findRouteParam } from '../../shared/route-params';

@Component({
  selector: 'app-test-result-history',
  imports: [TranslatePipe, DatePipe, RouterLink, MatIcon, MatProgressSpinner, ProjectNavComponent],
  templateUrl: './test-result-history.component.html',
  styleUrl: './test-result-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestResultHistoryComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly testRunService = inject(TestRunService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly statusKey = statusKey;
  protected readonly matrixIcon = matrixIcon;
  protected readonly sourceToSystemKey = sourceToSystemKey;
  protected readonly systemMeta = systemMeta;
  protected readonly environmentClass = environmentClass;

  protected readonly projectId: string;
  protected readonly testCaseId: string;

  protected readonly loading = signal(true);
  protected readonly loadError = signal(false);
  private readonly history = signal<TestCaseRunHistory | null>(null);

  protected readonly overviewLink: string[];
  /** Zurueck in die Testfall-Tabelle, aus der Historie und Detail geoeffnet werden. */
  protected readonly overviewQueryParams = { sicht: 'testfaelle' };

  protected readonly entries = computed(() => this.history()?.entries ?? []);
  protected readonly testCaseKey = computed(() => this.history()?.testCaseKey ?? '');
  protected readonly title = computed(() => this.history()?.title ?? '');

  /** Sparkline order mirrors the design: oldest → newest (the table stays newest first). */
  protected readonly sparkline = computed(() => [...this.entries()].reverse());

  protected readonly total = computed(() => this.entries().length);
  protected readonly passed = computed(() => this.countStatus('PASSED'));
  protected readonly failed = computed(() => this.countStatus('FAILED'));
  protected readonly passRate = computed(() => {
    const total = this.total();
    return total === 0 ? 0 : Math.round((this.passed() / total) * 100);
  });
  protected readonly passRateClass = computed(() => {
    const rate = this.passRate();
    return rate >= 80 ? 'good' : rate >= 50 ? 'warn' : 'bad';
  });

  protected readonly avgDuration = computed(() => {
    const durations = this.entries()
      .map((e) => e.durationSeconds)
      .filter((d): d is number => d !== null && d !== undefined);
    if (durations.length === 0) {
      return '—';
    }
    const avg = durations.reduce((sum, d) => sum + d, 0) / durations.length;
    return formatDurationSeconds(avg);
  });

  constructor() {
    this.projectId = this.findParam('projectId') ?? '';
    this.testCaseId = this.route.snapshot.paramMap.get('testCaseId') ?? '';
    this.overviewLink = ['/projects', this.projectId, 'test-results'];
  }

  ngOnInit(): void {
    this.testRunService
      .getRunHistory(this.projectId, this.testCaseId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (history) => {
          this.history.set(history);
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  /** RouterLink target of a run: the read-only run-detail page (NEX-729). */
  protected detailLink(entry: TestCaseRunHistoryEntry): string[] {
    return ['/projects', this.projectId, 'test-results', entry.runId];
  }

  protected formatDuration(seconds: number | null): string {
    return formatDurationSeconds(seconds);
  }

  private countStatus(status: TestCaseRunHistoryEntry['status']): number {
    return this.entries().filter((e) => e.status === status).length;
  }

  private findParam(name: string): string | null {
    return findRouteParam(this.route, name);
  }
}
